import { useMemo } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { useIsMapPageVisible, withoutHiddenMapPage } from '@shared/hooks/weddingMapPageVisibility';

type UseRegistryNavPagesArgs = Readonly<{
  /** A locked-out guest gets the already-filtered guest-site list and no app page. */
  isLockedOut: boolean;
  guestSitePages: ReadonlyArray<EventPageFragment>;
  /** `event.pages` plus the synthetic "Get the app" page. */
  pagesWithAppPage: ReadonlyArray<EventPageFragment>;
}>;

/**
 * The page list the Registry guest page's nav renders from.
 *
 * Extracted from `GuestRegistry.controller` so the Wedding Map gate here can
 * be tested: the controller is unreachable from a spec without a render
 * harness it does not have, so deleting the filter failed nothing and a
 * regression would have shipped silently.
 *
 * The gate matters because this list is **not** `useGetGuestSitePages`. Only
 * the locked-out branch and the hamburger menu go through that hook; the
 * inline nav on desktop Brannan renders the raw `event.pages` plus the app
 * page, so without filtering here a guest outside the rollout would see a Map
 * link into a route that sends them back to the guest site.
 */
export const useRegistryNavPages = ({ isLockedOut, guestSitePages, pagesWithAppPage }: UseRegistryNavPagesArgs): ReadonlyArray<EventPageFragment> => {
  const isMapPageVisible = useIsMapPageVisible();

  return useMemo(() => {
    if (isLockedOut) {
      return guestSitePages;
    }
    return withoutHiddenMapPage(pagesWithAppPage, isMapPageVisible);
  }, [isLockedOut, guestSitePages, pagesWithAppPage, isMapPageVisible]);
};
